import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  RanchPageViewEnum,
  selectRanchPageView,
} from '../../ranches/ranchesSlice';
import useIsBlueberryFeature from '../../scanDataApi/hooks/useIsBlueberryFeature';
import useScanData from '../../scanDataApi/hooks/useScanData';
import { ScanData } from '../../scanDataApi/scanDataDefinitions';
import { selectCurrFeature, selectCurrPointId } from '../../select/selectSlice';
import { setCurrNeighborPoints } from '../imageSlice';

const numNeighbors = 20; // number of neighboring points to find on either side of the current point

// Sets the neighboring points around the current point, for use in caching images in the image viewer
export function useFindNeighboringPoints() {
  const dispatch = useAppDispatch();
  const { scanData, blueberryData } = useScanData();
  const ranchPageView = useAppSelector(selectRanchPageView);
  const currFeature = useAppSelector(selectCurrFeature);
  const currPointId = useAppSelector(selectCurrPointId);
  const isBlueberry = useIsBlueberryFeature();
  const [neighbors, setNeighbors] = useState<ScanData[]>([]);

  // Pick current data based on whether blueberry or normal scan data feature is selected
  const currentData = useMemo(
    () => (isBlueberry ? blueberryData : scanData),
    [scanData, blueberryData, isBlueberry]
  );

  // Create a list of neighboring points to the current point
  useEffect(() => {
    if (
      ranchPageView === RanchPageViewEnum.ranchSummary ||
      !currentData.data ||
      !currPointId
    ) {
      // Clear neighbor points to avoid stale data showing up in image viewer
      setNeighbors([]);
      dispatch(setCurrNeighborPoints([]));
    } else {
      if (currentData && currPointId) {
        const index = currentData.data.findIndex(
          (point) => point.point_id === currPointId
        );

        if (index !== -1) {
          const start = Math.max(index - numNeighbors, 0);
          const end = Math.min(
            index + numNeighbors + 1,
            currentData.data.length
          );
          const foundNeighbors = currentData.data.slice(start, end);
          setNeighbors(foundNeighbors);
        } else {
          setNeighbors([]);
        }
      }
    }
  }, [currentData, currFeature, currPointId, ranchPageView]);

  useEffect(() => {
    if (currentData && neighbors.length > 0) {
      dispatch(setCurrNeighborPoints(neighbors));
    }
  }, [neighbors, currentData]);
}
